import { Injectable, inject } from '@angular/core';
import { SessionService } from '../mainServices/session';
import { environment } from '../../environment.dev';

// The type of event the server can send us about a record
type LiveEvent = 'create' | 'update' | 'enter' | 'leave' | 'delete';

// The shape of the function you pass when subscribing — it receives the event name and the record
type LiveCallback = (event: LiveEvent, object: any) => void;

/**
 * LiveQueryService — Real-time data updates over WebSocket
 *
 * HOW IT WORKS (plain English):
 *
 * Step 1 — connect()
 *   We open a WebSocket connection to the backend server.
 *   Once the socket is open we send a "connect" message with our
 *   applicationId, clientKey, and sessionToken so the server knows who we are.
 *
 * Step 2 — server replies "connected"
 *   The server confirms the connection and we are ready to subscribe.
 *
 * Step 3 — subscribeToClass()
 *   We tell the server: "I want to watch this database table (className)
 *   filtered by these conditions (where)."
 *   The server replies "subscribed" to confirm.
 *
 * Step 4 — real-time events arrive
 *   Whenever a record in that table changes the server pushes us a message:
 *   "create" → a new record was added
 *   "update" → an existing record was changed
 *   "enter"  → a record now matches our filter (e.g. was updated to match)
 *   "leave"  → a record no longer matches our filter
 *   "delete" → a record was deleted
 *   We look up the callback registered for that subscription and call it.
 *
 * Step 5 — unsubscribe() / close()
 *   We tell the server to stop sending events for a subscription,
 *   or close the whole connection on logout.
 */
@Injectable({ providedIn: 'root' })
export class LiveQueryService {
  private sessionService = inject(SessionService);

  // ─── Connection state ────────────────────────────────────────────────────

  private socket?: WebSocket;

  // True once the WebSocket TCP connection is open (socket handshake done)
  private isConnected = false;

  // True once the server replies "connected" (LiveQuery handshake done)
  // We can only subscribe AFTER this is true
  private isLiveQueryConnected = false;

  // ─── Subscription registry ───────────────────────────────────────────────

  // Maps requestId → callback function.
  // When an event arrives we look up the requestId to find the right callback.
  private subscriptions = new Map<number, LiveCallback>();

  // Recycled requestIds from unsubscribed queries — reused to avoid waste
  private freeIds: number[] = [];

  // ─── Queues ──────────────────────────────────────────────────────────────

  // Messages we tried to send before the socket was ready — sent once it opens
  private pendingMessages: any[] = [];

  // Subscriptions we tried to register before the server said "connected"
  // — sent once the LiveQuery handshake finishes
  private pendingSubscriptions: {
    requestId: number;
    className: string;
    where: any;
    callback: LiveCallback;
  }[] = [];

  // Maximum number of queued messages — safety cap for flaky connections
  private readonly MAX_QUEUE = 500;

  // ─── Constructor ─────────────────────────────────────────────────────────

  constructor() {
    // Listen for the browser going offline/online once (the service is a singleton)
    window.addEventListener('offline', () => {
      console.warn('[LiveQuery] Browser went offline');
    });

    window.addEventListener('online', () => {
      // Browser came back online — re-open the connection automatically
      console.log('[LiveQuery] Browser back online. Reconnecting…');
      this.connect();
    });
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * STEP 1 — Open the WebSocket and perform the LiveQuery handshake.
   *
   * Safe to call multiple times — if a socket is already open or connecting
   * we do nothing (the guard at the top stops double connections).
   *
   * @param onOpen      called once the socket TCP connection is open
   * @param onReconnect called after the socket drops and we start retrying
   */
  connect(onOpen?: () => void, onReconnect?: () => void) {

    // ── Guard: only one socket at a time ──
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      onOpen?.();
      return;
    }

    // ── Open the WebSocket TCP connection ──
    this.socket = new WebSocket(environment.wss);

    // ── Socket opened (TCP handshake done) ──
    this.socket.onopen = () => {
      console.log('[LiveQuery] WebSocket opened');
      this.isConnected = true;
      this.isLiveQueryConnected = false; // not yet — need to wait for "connected"

      // Send the LiveQuery "connect" handshake so the server knows who we are.
      // We defer one tick (setTimeout 0) to guarantee readyState is OPEN.
      setTimeout(() => {
        this.send({
          op: 'connect',
          applicationId: environment.appId,
          clientKey: environment.clientKey,
          sessionToken: this.sessionService.getSessionToken(),
        });
      }, 0);

      // Send any messages that were queued before the socket opened
      this.flushPendingMessages();
      onOpen?.();
    };

    // ── Message received from server ──
    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.op) {

        // STEP 2 — Server confirmed our "connect" message
        case 'connected':
          console.log('[LiveQuery] Connection acknowledged');
          this.isLiveQueryConnected = true;
          // Now we're fully ready — send any subscriptions that were waiting
          this.flushPendingSubscriptions();
          break;

        // Server confirmed one of our "subscribe" messages
        case 'subscribed':
          console.log(`[LiveQuery] Subscribed (requestId: ${data.requestId})`);
          break;

        // Server reported an error
        case 'error':
          console.error('[LiveQuery] Error from server:', data);
          break;

        // STEP 4 — A real-time event arrived (create / update / enter / leave / delete)
        default: {
          const liveEvents: LiveEvent[] = ['create', 'update', 'enter', 'leave', 'delete'];
          const isLiveEvent = typeof data.op === 'string' && liveEvents.includes(data.op);

          if (isLiveEvent) {
            // Find the callback registered for this subscription and call it
            const handler = this.subscriptions.get(data.requestId);
            handler?.(data.op as LiveEvent, data.object);
          }
          break;
        }
      }
    };

    // ── Socket closed (network drop, server restart, etc.) ──
    this.socket.onclose = () => {
      console.warn('[LiveQuery] Disconnected');
      this.isConnected = false;
      this.isLiveQueryConnected = false;

      if (navigator.onLine) {
        // We have internet but the socket dropped — retry in 2 seconds
        console.log('[LiveQuery] Online — retrying in 2s');
        setTimeout(() => this.connect(undefined, onReconnect), 2000);
      } else {
        // No internet — wait until the browser comes back online
        console.warn('[LiveQuery] Offline — waiting for network');
        const handleOnline = () => {
          window.removeEventListener('online', handleOnline);
          this.connect(undefined, onReconnect);
        };
        window.addEventListener('online', handleOnline);
      }

      onReconnect?.();
    };

    // ── Socket-level error ──
    this.socket.onerror = (err) => {
      console.error('[LiveQuery] WebSocket error:', err);
    };
  }

  /**
   * STEP 3 — Tell the server to watch a database table and call our callback
   * whenever a matching record changes.
   *
   * @param requestId  unique number to identify this subscription (auto-generated if omitted)
   * @param className  the Parse database table name e.g. "ChatConversation"
   * @param where      filter object e.g. { groupChat: { objectId: "abc" } }
   * @param callback   function called with (event, record) on every change
   * @returns the requestId — save it so you can unsubscribe later
   */
  subscribeToClass(requestId: number, className: string, where: any, callback: LiveCallback): number;
  subscribeToClass(className: string, where: any, callback: LiveCallback): number;
  subscribeToClass(
    a: number | string,
    b: string | any,
    c?: any,
    d?: LiveCallback
  ): number {
    let requestId: number;
    let className: string;
    let where: any;
    let callback: LiveCallback;

    if (typeof a === 'number') {
      requestId = a;
      className = b as string;
      where = c;
      callback = d!;
    } else {
      requestId = this.allocRequestId();
      className = a;
      where = b;
      callback = c;
    }

    if (this.isConnected && this.isLiveQueryConnected) {
      // Connection is ready — subscribe immediately
      this._subscribe(requestId, className, where, callback);
    } else {
      // Not ready yet — queue it and it will be sent once the handshake finishes
      this.pendingSubscriptions.push({ requestId, className, where, callback });
    }

    return requestId;
  }

  /**
   * STEP 5a — Stop watching a subscription.
   * Call this when the component that subscribed is destroyed.
   *
   * @param requestId the value returned by subscribeToClass()
   */
  unsubscribe(requestId: number) {
    this.send({ op: 'unsubscribe', requestId });
    this.subscriptions.delete(requestId);
    this.releaseRequestId(requestId);
  }

  /**
   * STEP 5b — Close the entire connection and clear all state.
   * Call this on logout.
   */
  close() {
    this.pendingMessages = [];
    this.pendingSubscriptions = [];
    this.subscriptions.clear();
    try { this.socket?.close(); } catch { /* ignored */ }
    this.socket = undefined;
    this.isConnected = false;
    this.isLiveQueryConnected = false;
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  /**
   * Send the actual "subscribe" message to the server.
   * Registers the callback locally first so we can find it when events arrive.
   */
  private _subscribe(requestId: number, className: string, where: any, callback: LiveCallback) {
    // Save callback so we can call it when an event arrives for this requestId
    this.subscriptions.set(requestId, callback);

    this.send({
      op: 'subscribe',
      requestId,
      query: { className, where },
      sessionToken: this.sessionService.getSessionToken(),
    });
  }

  /**
   * Send a message through the WebSocket.
   * If the socket isn't ready yet, queue the message and send it later.
   */
  private send(data: any) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return;
    }

    // Don't queue a second "connect" if one is already waiting
    if (data.op === 'connect' && this.pendingMessages.some(m => m.op === 'connect')) {
      return;
    }

    // Cap the queue so memory doesn't grow forever on a bad connection
    if (this.pendingMessages.length >= this.MAX_QUEUE) {
      this.pendingMessages.shift(); // drop the oldest message
    }

    this.pendingMessages.push(data);

    if (data.op !== 'connect') {
      console.warn('[LiveQuery] Socket not ready — queued:', data);
    }
  }

  /** Send all messages that were queued while the socket was not yet open */
  private flushPendingMessages() {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const msgs = this.pendingMessages.splice(0);
    for (const m of msgs) this.send(m);
  }

  /** Send all subscriptions that were queued while the handshake was not yet done */
  private flushPendingSubscriptions() {
    if (!this.isLiveQueryConnected) return;
    const subs = this.pendingSubscriptions.splice(0);
    for (const s of subs) this._subscribe(s.requestId, s.className, s.where, s.callback);
  }

  // ─── RequestId helpers ───────────────────────────────────────────────────
  // Every subscription needs a unique number (requestId) so the server and
  // the client can match incoming events to the right callback.

  private randomRequestId(): number {
    if ('crypto' in globalThis && 'getRandomValues' in crypto) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] & 0x7fffffff; // gives a random positive 31-bit integer
    }
    return (Math.random() * 0x7fffffff) | 0;
  }

  private allocRequestId(): number {
    // Reuse a freed id if available, otherwise generate a new random one
    if (this.freeIds.length) return this.freeIds.pop()!;
    let id: number;
    do { id = this.randomRequestId(); }
    while (this.subscriptions.has(id)); // make sure it's not already in use
    return id;
  }

  private releaseRequestId(id: number) {
    // Return the id to the pool so it can be reused later
    this.freeIds.push(id);
  }
}
